import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, Lock, Search, X, Star, Flame } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Candidate, Evaluation, EvaluationComment } from '../lib/supabase';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';

interface CandidateWithEvaluationInfo extends Candidate {
  evaluations: Evaluation[];
}

export default function PublicDiscussion() {
  const [candidates, setCandidates] = useState<CandidateWithEvaluationInfo[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateWithEvaluationInfo | null>(null);
  const [selectedEvaluation, setSelectedEvaluation] = useState<Evaluation | null>(null);
  const [comments, setComments] = useState<EvaluationComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSecretComment, setIsSecretComment] = useState(false);
  const [isSecretReply, setIsSecretReply] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserType, setCurrentUserType] = useState<'evaluator' | 'candidate' | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedTopics, setCollapsedTopics] = useState<Record<string, boolean>>({});
  const [evaluatorById, setEvaluatorById] = useState<Record<string, {name: string; username?: string}>>({});
  const [evalToEvaluator, setEvalToEvaluator] = useState<Record<string, string>>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});
  const [recentCounts, setRecentCounts] = useState<Record<string, number>>({});
  const [recentLikeCounts, setRecentLikeCounts] = useState<Record<string, number>>({});
  const [lastCommentAt, setLastCommentAt] = useState<Record<string, string>>({});
  const [sortBy, setSortBy] = useState<'rating' | 'ratingCount' | 'comments' | 'newest'>('comments');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    console.log('selectedCandidate changed:', selectedCandidate?.id, selectedCandidate?.name);
    if (selectedCandidate) {
      loadComments();
    } else {
      setComments([]);
    }
  }, [selectedCandidate]);

  const loadData = async () => {
    try {
      // Get current user type
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
        setCurrentUserEmail(user.email ?? null);
        // Check admin
        try {
          const { data: adminRow } = await supabase
            .from('admin_users')
            .select('user_id')
            .eq('user_id', user.id)
            .maybeSingle();
          setIsAdmin(!!adminRow);
        } catch {}
        const { data: candidateData } = await supabase
          .from('candidates')
          .select('*')
          .eq('email', user.email)
          .maybeSingle();
        
        if (candidateData) {
          setCurrentUserType('candidate');
        } else {
          setCurrentUserType('evaluator');
        }
      }

      // Load all candidates with their evaluations
      const { data: candidatesData, error: candidatesError } = await supabase
        .from('candidates')
        .select(`
          *,
          evaluations (*)
        `)
        .order('created_at', { ascending: false });

      if (candidatesError) throw candidatesError;
      setCandidates(candidatesData || []);

      // Load comment counts per candidate（総数と直近5分）
      try {
        const { data: evals } = await supabase
          .from('evaluations')
          .select('id, candidate_id');
        const evalToCandidate = new Map<string, string>();
        (evals || []).forEach((e: any) => evalToCandidate.set(e.id, e.candidate_id));

        const { data: comments } = await supabase
          .from('evaluation_comments')
          .select('evaluation_id, created_at');
        const counts: Record<string, number> = {};
        const recent: Record<string, number> = {};
        const lastAt: Record<string, string> = {};
        const now = Date.now();
        (comments || []).forEach((r: any) => {
          const cid = evalToCandidate.get(r.evaluation_id);
          if (!cid) return;
          counts[cid] = (counts[cid] || 0) + 1;
          if (!lastAt[cid]) lastAt[cid] = r.created_at as string;
          const t = new Date(r.created_at as string).getTime();
          if (now - t <= 5 * 60 * 1000) {
            recent[cid] = (recent[cid] || 0) + 1;
          }
        });
        setCommentCounts(counts);
        setRecentCounts(recent);
        setLastCommentAt(lastAt);
      } catch {}

      // Load recent likes in last 5 minutes per candidate（liked=true && updated_at within 5m）
      try {
        const since = new Date(Date.now() - 5 * 60 * 1000).toISOString();
        const { data: recentLikes } = await supabase
          .from('evaluations')
          .select('candidate_id, updated_at')
          .eq('liked', true)
          .gte('updated_at', since);
        const rl: Record<string, number> = {};
        (recentLikes || []).forEach((row: any) => {
          const cid = row.candidate_id as string;
          rl[cid] = (rl[cid] || 0) + 1;
        });
        setRecentLikeCounts(rl);
      } catch {}
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    if (!selectedCandidate) {
      console.log('No candidate selected');
      return;
    }

    console.log('Loading comments for candidate:', selectedCandidate.id, selectedCandidate.name);
    setCommentsLoading(true);
    setError(null);
    
    try {
      // Load all comments for all evaluations of this candidate
      const { data: evaluations, error: evalError } = await supabase
        .from('evaluations')
        .select('id, evaluator_id')
        .eq('candidate_id', selectedCandidate.id);

      if (evalError) {
        console.error('Error loading evaluations:', evalError);
        throw evalError;
      }

      console.log('Found evaluations:', evaluations?.length || 0);

      if (!evaluations || evaluations.length === 0) {
        console.log('No evaluations found for this candidate');
        setComments([]);
        setCommentsLoading(false);
        return;
      }

      const evaluationIds = evaluations.map(e => e.id);
      const evalMap: Record<string, string> = {};
      (evaluations || []).forEach((e: any) => { evalMap[e.id] = e.evaluator_id; });
      setEvalToEvaluator(evalMap);
      // Load evaluator display names
      const evaluatorIds = Array.from(new Set(evaluations.map((e: any) => e.evaluator_id).filter(Boolean)));
      if (evaluatorIds.length) {
        const { data: infos } = await supabase
          .from('evaluators')
          .select('id, name, username')
          .in('id', evaluatorIds);
        const map: Record<string, {name: string; username?: string}> = {};
        (infos || []).forEach((ev: any) => { map[ev.id] = { name: ev.name, username: ev.username || undefined }; });
        setEvaluatorById(map);
      } else {
        setEvaluatorById({});
      }
      let combined: EvaluationComment[] = [];

      if (
        isAdmin ||
        (currentUserType === 'candidate' && currentUserEmail && selectedCandidate?.email && currentUserEmail === selectedCandidate.email)
      ) {
        const { data, error } = await supabase
          .from('evaluation_comments')
          .select('*')
          .in('evaluation_id', evaluationIds)
          .order('created_at', { ascending: true });
        if (error) throw error;
        combined = (data || []) as any;
      } else {
        const { data: pub, error: e1 } = await supabase
          .from('evaluation_comments')
          .select('*')
          .in('evaluation_id', evaluationIds)
          .eq('is_secret', false)
          .order('created_at', { ascending: true });
        if (e1) throw e1;
        combined = (pub || []) as any;

        if (currentUserId) {
          const { data: own, error: e2 } = await supabase
            .from('evaluation_comments')
            .select('*')
            .in('evaluation_id', evaluationIds)
            .eq('is_secret', true)
            .eq('user_id', currentUserId)
            .order('created_at', { ascending: true });
          if (e2) throw e2;
          combined = combined.concat((own || []) as any);
        }
      }

      console.log('Loaded comments (filtered server-side):', combined.length);
      const visible = (combined || []).filter(c => canViewComment(c));
      setComments(visible);
    } catch (err: any) {
      console.error('loadComments error:', err);
      setError(err.message);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if ((!newComment.trim() && !newTopic.trim()) || !selectedCandidate || !currentUserType) return;

    setCommentLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      // Get or create an evaluation for commenting
      let evaluationId: string;
      
      if (selectedEvaluation) {
        evaluationId = selectedEvaluation.id;
      } else {
        // First, check if current user has an evaluator record
        const { data: evaluatorData } = await supabase
          .from('evaluators')
          .select('id')
          .eq('email', user.email)
          .maybeSingle();
          
        if (!evaluatorData) {
          throw new Error('参加者として登録されていません。参加者としてログインしてください。');
        }
        
        // Check if evaluation already exists for this user
        const { data: existingEval } = await supabase
          .from('evaluations')
          .select('id')
          .eq('candidate_id', selectedCandidate.id)
          .eq('evaluator_id', evaluatorData.id)
          .maybeSingle();
          
        if (existingEval) {
          evaluationId = existingEval.id;
        } else {
          // Create a minimal evaluation
          const { data: newEval, error: evalError } = await supabase
            .from('evaluations')
            .insert({
              candidate_id: selectedCandidate.id,
              evaluator_id: evaluatorData.id,
              rating: 5,
              liked: false,
              want_to_participate: false
            })
            .select()
            .single();
            
          if (evalError) throw evalError;
          evaluationId = newEval.id;
        }
        
        // Update selected evaluation
        setSelectedEvaluation({ id: evaluationId } as Evaluation);
      }

      const { error } = await supabase
        .from('evaluation_comments')
        .insert({
          evaluation_id: evaluationId,
          user_id: user.id,
          user_type: currentUserType,
          content: newComment.trim() || `新しいトピック: ${newTopic.trim()}`,
          is_secret: isSecretComment,
          parent_comment_id: null,
          topic: newTopic.trim() || 'general'
        });

      if (error) throw error;

      setNewComment('');
      setNewTopic('');
      setIsSecretComment(false);
      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleSubmitReply = async (parentId: string, topic: string) => {
    if (!replyContent.trim() || !currentUserType) return;

    setCommentLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      // Find the parent comment to get its evaluation_id
      const parentComment = comments.find(c => c.id === parentId);
      if (!parentComment) throw new Error('親コメントが見つかりません');

      const { error } = await supabase
        .from('evaluation_comments')
        .insert({
          evaluation_id: parentComment.evaluation_id,
          user_id: user.id,
          user_type: currentUserType,
          content: replyContent.trim(),
          is_secret: isSecretReply,
          parent_comment_id: parentId,
          topic: topic
        });

      if (error) throw error;

      setReplyContent('');
      setReplyingTo(null);
      setIsSecretReply(false);
      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const filteredCandidates = candidates.filter(candidate => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      candidate.name.toLowerCase().includes(term) ||
      candidate.affiliation.toLowerCase().includes(term) ||
      candidate.department?.toLowerCase().includes(term) ||
      candidate.research_topic.toLowerCase().includes(term)
    );
  });

  const organizeComments = (comments: EvaluationComment[]) => {
    const topicGroups: { [key: string]: EvaluationComment[] } = {};
    const replies: { [key: string]: EvaluationComment[] } = {};

    comments.forEach(comment => {
      if (comment.parent_comment_id) {
        if (!replies[comment.parent_comment_id]) {
          replies[comment.parent_comment_id] = [];
        }
        replies[comment.parent_comment_id].push(comment);
      } else {
        const topic = comment.topic || 'general';
        if (!topicGroups[topic]) {
          topicGroups[topic] = [];
        }
        topicGroups[topic].push(comment);
      }
    });

    return { topicGroups, replies };
  };

  const formatCommentTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('ja-JP', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleDateString('ja-JP', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  // Visibility rule for secret comments: only author and the presenter of the selected research can view
  const canViewComment = (comment: EvaluationComment) => {
    if (!comment.is_secret) return true;
    if (currentUserId && comment.user_id === currentUserId) return true;
    if (
      currentUserType === 'candidate' &&
      currentUserEmail &&
      selectedCandidate &&
      selectedCandidate.email &&
      currentUserEmail === selectedCandidate.email
    ) {
      return true;
    }
    return false;
  };

  const renderComment = (comment: EvaluationComment, allReplies: { [key: string]: EvaluationComment[] }, level = 0) => (
    <div key={comment.id} className={`${level > 0 ? 'ml-8 border-l-4 border-primary/40 pl-6' : ''} mb-5`}>
      <div className="flex justify-start mb-4">
        <div
          className={`max-w-[85%] rounded-lg p-5 shadow-sm transition-all duration-200 hover:shadow-md ${
            comment.user_type === 'evaluator'
              ? 'bg-blue-50 border border-blue-200 text-blue-900'
              : 'bg-green-50 border border-green-200 text-green-900'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                comment.user_type === 'evaluator' ? 'bg-blue-500' : 'bg-green-500'
              }`} />
              <span className="text-sm font-semibold">
                {comment.user_type === 'candidate'
                  ? (selectedCandidate?.username || selectedCandidate?.name || '研究者')
                  : (() => {
                      const eid = evalToEvaluator[comment.evaluation_id as any];
                      if (eid && evaluatorById[eid]) {
                        const info = evaluatorById[eid];
                        return info.username || info.name;
                      }
                      return '参加者';
                    })()
                }
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {comment.is_secret && (
                <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-orange-100 text-orange-800">
                  <Lock className="h-3 w-3" />
                  <span className="text-xs font-medium">プライベート</span>
                </div>
              )}
              <span className="text-xs text-muted-foreground">
                {formatCommentTime(comment.created_at)}
              </span>
            </div>
          </div>
          
          <div className={`mb-3 ${comment.is_secret ? 'bg-gray-100 border border-gray-300 rounded p-3' : ''}`}>
            <p className="text-base leading-relaxed whitespace-pre-wrap break-words overflow-wrap-anywhere">
              {comment.content}
            </p>
          </div>
          
          <div className="flex justify-between items-center">
            <Button
              variant="secondary"
              size="sm"
              className="h-8 px-3 text-xs"
              onClick={() => setReplyingTo(comment.id)}
            >
              <MessageCircle className="h-3 w-3 mr-1" />
              返信を書く
            </Button>
          </div>
        </div>
      </div>
      
      {/* Reply form */}
      {replyingTo === comment.id && (
        <div className="mb-4 ml-4">
          <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border">
            <div className="flex items-center space-x-2">
              <MessageCircle className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">返信を投稿</span>
            </div>
            <Textarea
              placeholder="返信内容を入力してください..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              rows={3}
              className="resize-none"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="secret-reply"
                  checked={isSecretReply}
                  onCheckedChange={(checked) => setIsSecretReply(!!checked)}
                />
                <Label htmlFor="secret-reply" className="flex items-center space-x-2 cursor-pointer text-sm">
                  <span className="text-muted-foreground">プライベート返信</span>
                  {isSecretReply && (
                    <Lock className="h-3 w-3 text-orange-500" />
                  )}
                </Label>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyContent('');
                    setIsSecretReply(false);
                  }}
                >
                  キャンセル
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleSubmitReply(comment.id, comment.topic)}
                  disabled={!replyContent.trim() || commentLoading}
                >
                  {commentLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  返信を投稿
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Nested replies */}
      <div className="space-y-2">
        {allReplies[comment.id]?.map(reply => renderComment(reply, allReplies, level + 1))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">公開ディスカッション</h1>
        <p className="text-muted-foreground mt-1">
          すべての研究発表についてディスカッションができます
        </p>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="研究者名・所属・研究課題で検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-8 w-8 p-0"
                  onClick={() => setSearchTerm('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">並び替え:</span>
              <select
                className="h-9 border rounded-md px-2 bg-background"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
              >
                <option value="comments">コメント数</option>
                <option value="rating">平均評価</option>
                <option value="ratingCount">評価件数</option>
                <option value="newest">新着</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        <h2 className="text-xl font-semibold">研究発表一覧</h2>
        <div className="space-y-3">
          {(() => {
            const ordered = [...filteredCandidates];
            if (currentUserType === 'candidate' && currentUserEmail) {
              ordered.sort((a, b) => {
                const aOwn = a.email === currentUserEmail ? 1 : 0;
                const bOwn = b.email === currentUserEmail ? 1 : 0;
                return bOwn - aOwn; // own first
              });
            }
            // Recent activity priority (comments + likes within 5 minutes)
            ordered.sort((a, b) => {
              const sa = (recentCounts[a.id] || 0) + (recentLikeCounts[a.id] || 0);
              const sb = (recentCounts[b.id] || 0) + (recentLikeCounts[b.id] || 0);
              return sb - sa;
            });
            // Apply selected sort
            ordered.sort((a, b) => {
              const ra = (recentCounts[a.id] || 0) + (recentLikeCounts[a.id] || 0);
              const rb = (recentCounts[b.id] || 0) + (recentLikeCounts[b.id] || 0);
              if (ra !== rb) return 0; // preserve prior recent ordering
              if (sortBy === 'comments') {
                return (commentCounts[b.id] || 0) - (commentCounts[a.id] || 0);
              }
              if (sortBy === 'rating') {
                const avg = (c: any) => {
                  const rated = (c.evaluations || []).filter((e: any) => (e.rating || 0) > 0);
                  const count = rated.length;
                  return count > 0 ? rated.reduce((s: number, e: any) => s + Math.max(0, Math.min(5, e.rating || 0)), 0) / count : 0;
                };
                return avg(b) - avg(a);
              }
              if (sortBy === 'ratingCount') {
                const cnt = (c: any) => (c.evaluations || []).filter((e: any) => (e.rating || 0) > 0).length;
                return cnt(b) - cnt(a);
              }
              // newest fallback
              const ta = (a as any).created_at ? new Date((a as any).created_at).getTime() : 0;
              const tb = (b as any).created_at ? new Date((b as any).created_at).getTime() : 0;
              return tb - ta;
            });
            return ordered;
          })().map((candidate) => {
            const isSelected = selectedCandidate?.id === candidate.id;
            return (
              <div key={candidate.id} className="space-y-3 w-full">
                <Card
                  className={`w-full cursor-pointer transition-all ${
                    isSelected
                      ? 'ring-2 ring-primary bg-blue-50 shadow-md border-primary/20'
                      : 'hover:shadow-md hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedCandidate(null);
                      setSelectedEvaluation(null);
                      return;
                    }
                    setError(null);
                    setNewComment('');
                    setNewTopic('');
                    setReplyingTo(null);
                    setReplyContent('');
                    setSelectedCandidate(candidate);
                    setSelectedEvaluation(candidate.evaluations[0] || null);
                  }}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{candidate.research_topic}</CardTitle>
                    <CardDescription>
                      {candidate.name} - {candidate.affiliation}
                      {candidate.department && ` / ${candidate.department}`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const rated = (candidate.evaluations || []).filter((e: any) => (e.rating || 0) > 0);
                      const count = rated.length;
                      const avg = count > 0 ? (rated.reduce((s: number, e: any) => s + Math.min(5, Math.max(0, e.rating || 0)), 0) / count) : 0;
                      const rounded = Math.max(0, Math.min(5, Math.round(avg * 2) / 2));
                      const cmTotal = commentCounts[candidate.id] || 0;
                      const rc = recentCounts[candidate.id] || 0;
                      const rl = recentLikeCounts[candidate.id] || 0;
                      const recentScore = rc + rl;
                      const heatClass = recentScore > 0 ? 'border-orange-300 bg-orange-50' : cmTotal >= 8 ? 'border-red-300 bg-red-50' : cmTotal >= 4 ? 'border-yellow-200 bg-yellow-50' : 'border-border';
                      return (
                        <div className={`flex items-center justify-between border ${heatClass} rounded-md px-3 py-2`}>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center">
                              {[1,2,3,4,5].map(i => {
                                const fill = rounded >= i ? 1 : (rounded >= i - 0.5 ? 0.5 : 0);
                                if (fill === 1) {
                                  return (
                                    <svg key={i} viewBox="0 0 24 24" className="h-4 w-4 text-yellow-500" fill="currentColor" aria-hidden="true">
                                      <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                                    </svg>
                                  );
                                }
                                if (fill === 0.5) {
                                  return (
                                    <span key={i} className="relative inline-block h-4 w-4 align-middle">
                                      <svg viewBox="0 0 24 24" className="absolute inset-0 h-4 w-4 text-muted-foreground" fill="currentColor" aria-hidden="true">
                                        <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                                      </svg>
                                      <span className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                                        <svg viewBox="0 0 24 24" className="h-4 w-4 text-yellow-500" fill="currentColor" aria-hidden="true">
                                          <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                                        </svg>
                                      </span>
                                    </span>
                                  );
                                }
                                return (
                                  <svg key={i} viewBox="0 0 24 24" className="h-4 w-4 text-muted-foreground" fill="currentColor" aria-hidden="true">
                                    <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                                  </svg>
                                );
                              })}
                              <span className="ml-2 text-sm text-foreground">{avg.toFixed(1)} / 5</span>
                            </div>
                            <Badge variant="secondary">評価数: {count}</Badge>
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <MessageCircle className="h-3 w-3" />{cmTotal}
                            </Badge>
                            {recentScore > 0 && (
                              <span className="inline-flex items-center text-xs rounded-full px-2 py-0.5 bg-orange-100 text-orange-700">
                                <Flame className="h-3 w-3 mr-1" /> 最近5分: 💬{rc}{rl ? ` ❤️${rl}` : ''}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center text-sm text-muted-foreground">
                            <MessageCircle className="h-4 w-4 mr-1 text-primary" />
                            <span>{cmTotal}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                {isSelected && (
                  <Card
                    className="h-full w-full"
                    onTouchStart={(e) => {
                      const t = e.changedTouches[0];
                      (e.currentTarget as any)._sx = t.clientX;
                      (e.currentTarget as any)._sy = t.clientY;
                    }}
                    onTouchEnd={(e) => {
                      const t = e.changedTouches[0];
                      const sx = (e.currentTarget as any)._sx || 0;
                      const sy = (e.currentTarget as any)._sy || 0;
                      const dx = t.clientX - sx;
                      const dy = Math.abs(t.clientY - sy);
                      if (dx > 50 && dy < 40) {
                        setSelectedCandidate(null);
                        setSelectedEvaluation(null);
                      }
                    }}
                  >
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <MessageCircle className="h-5 w-5 mr-2" />
                        ディスカッション
                      </CardTitle>
                      <CardDescription>
                        {candidate.research_topic}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-4 max-h-none overflow-visible md:max-h-[600px] md:overflow-y-auto">
                        {commentsLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                          </div>
                        ) : comments.length === 0 ? (
                          <p className="text-muted-foreground text-center py-4">
                            まだコメントがありません。最初のコメントを投稿してください。
                          </p>
                        ) : (
                          (() => {
                            const { topicGroups, replies } = organizeComments(comments);
                            return Object.entries(topicGroups).map(([topic, topicComments]) => (
                              <div key={topic} className="space-y-4">
                                <div className="flex items-center justify-between">
                                  <div className="font-semibold text-foreground">
                                    {topic === 'general' ? '💬 一般的な質問・コメント' : `📋 ${topic}`}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">{topicComments.length}件</span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setCollapsedTopics(prev => ({ ...prev, [topic]: !prev[topic] }))}
                                    >
                                      {collapsedTopics[topic] ? '展開' : '折りたたむ'}
                                    </Button>
                                  </div>
                                </div>
                                {!collapsedTopics[topic] && (
                                  <div className="space-y-4">
                                    {topicComments.map(comment => 
                                      renderComment(comment, replies, 0)
                                    )}
                                  </div>
                                )}
                              </div>
                            ));
                          })()
                        )}
                      </div>

                      <div className="space-y-4 border-t border-border pt-4">
                        <div className="space-y-2">
                          <Label htmlFor="new-topic" className="text-sm font-medium text-foreground">
                            新しいトピック（任意）
                          </Label>
                          <Input
                            id="new-topic"
                            placeholder="例: 研究手法について、データ収集について..."
                            value={newTopic}
                            onChange={(e) => setNewTopic(e.target.value)}
                          />
                        </div>
                        <Textarea
                          placeholder="コメントを入力..."
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          rows={3}
                          className="resize-none"
                        />
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="secret-comment"
                              checked={isSecretComment}
                              onCheckedChange={(checked) => setIsSecretComment(!!checked)}
                            />
                            <Label htmlFor="secret-comment" className="flex items-center space-x-2 cursor-pointer">
                              <span className="text-sm text-muted-foreground">
                                プライベートコメント
                              </span>
                              {isSecretComment && (
                                <Lock className="h-3 w-3 text-orange-500" />
                              )}
                            </Label>
                          </div>
                          
                    <Button
                      onClick={handleSubmitComment}
                      disabled={(!newComment.trim() && !newTopic.trim()) || commentLoading}
                    >
                      {commentLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      投稿する
                    </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
